import { Injectable, inject } from "@angular/core";
import { ServerDefinitionsBase } from "@logex/framework/lg-application";
import { StringKeyOf } from "@logex/framework/types";
import { Dictionary, sortBy } from "lodash";
import { map } from "rxjs";
import { SharedDefinitionsApiService } from "./shared-definitions-api.service";
import {
    SharedDefinitionEntry,
    DefinitionApplicationInstance,
    DefinitionProduct,
    DefinitionJobTitleCategory,
    ISharedDefinitions,
    DefinitionApplicationGroup,
    DefinitionApplicationRole,
    DefinitionResourceType,
    DefinitionTag
} from "./shared-definitions.types";

export type DefinitionKey = StringKeyOf<ISharedDefinitions>;

@Injectable({
    providedIn: "root"
})
export class SharedDefinitions
    extends ServerDefinitionsBase<ISharedDefinitions>
    implements ISharedDefinitions
{
    private _definitionsGateway = inject(SharedDefinitionsApiService);

    // ----------------------------------------------------------------------------------
    // Fields
    applicationInstance: Dictionary<DefinitionApplicationInstance> = this.generate<
        DefinitionApplicationInstance,
        Dictionary<DefinitionApplicationInstance>
    >({
        generator: () => this._definitionsGateway.getApplicationInstance()
    });

    applicationGroup: Dictionary<DefinitionApplicationGroup> = this.generate<
        DefinitionApplicationGroup,
        Dictionary<DefinitionApplicationGroup>
    >({
        generator: () => this._definitionsGateway.getApplicationGroup()
    });

    applicationRole: Dictionary<DefinitionApplicationRole> = this.generate<
        DefinitionApplicationRole,
        Dictionary<DefinitionApplicationRole>
    >({
        generator: () => this._definitionsGateway.getApplicationRole()
    });

    jobTitleCategory: Dictionary<DefinitionJobTitleCategory> = this.generate<
        DefinitionJobTitleCategory,
        Dictionary<DefinitionJobTitleCategory>
    >({
        generator: () => this._definitionsGateway.getJobTitleCategory()
    });

    product: Dictionary<DefinitionProduct> = this.generate<
        DefinitionProduct,
        Dictionary<DefinitionProduct>
    >({
        generator: () => {
            return this._definitionsGateway.getProduct().pipe(
                map(products => {
                    Object.values(products).forEach(p => {
                        const isAddon = p.parentCode !== null;
                        if (isAddon) {
                            const parentProduct = products[p.parentCode];
                            p.name = `${parentProduct.name} - ${p.name}`;
                        }
                    });
                    return products;
                })
            );
        }
    });

    resourceType: Dictionary<DefinitionResourceType> = this.generate<
        DefinitionResourceType,
        Dictionary<DefinitionResourceType>
    >({
        generator: () => this._definitionsGateway.getResourceType()
    });

    tag: Dictionary<DefinitionTag> = this.generate<
        DefinitionTag,
        Dictionary<DefinitionTag>
    >({
        generator: () => this._definitionsGateway.getTags()
    });

    constructor() {
        super();
        this.init();
    }

    getDisplayName(sectionName: DefinitionKey, id: number | string): string {
        const entry = this.getEntry(sectionName, id);

        return entry ? entry.name : "";
    }

    getSortedSectionData(sectionName: DefinitionKey): SharedDefinitionEntry[] {
        const data = this.getSection(sectionName).data;
        return data ? sortBy(Object.values(data), item => item.name.toLowerCase()) : [];
    }
}
